import { useState, useEffect } from "react";
import { bindActionCreators, Dispatch } from "redux";
import { connect, RootStateOrAny } from "react-redux";

import { Typography } from "antd";

import { ErrorResponse } from "utils/types";
import { sortArrayByKey, getDisplayCompanyId } from "utils/helpers";
import notifier from "utils/notifier";

import { printProviderMemberReport } from "store/authorization/hcc/actions";
import { getProviderHccMembersLob } from "store/authorization/provider/actions";
import { getProviderReport, getDownloadDocument } from "store/eligibility/gap/actions";
import { getMemberMeasures } from "store/authorization/hcc/actions";
import { getLastRunDate } from "store/eligibility/gap/actions";
import GapMembersSection from "components/Hcc/GapMembers";
import { get as getProviderKeyListWDates, getDistinct as getDistinctProviderKeyListWDates } from "store/app/providerKeyListWDates/actions";
//eligibility info page related
import { search } from 'store/eligibility/actions';
import { check as checkSurvey } from 'store/authorization/survey/actions';
import { createStatistic } from "store/app/actions";

import GapMembersPageProps from "./Proptypes";
import ProviderSurveyModal from './Modals';
import moment from "moment";
import { Company } from "utils/enums";

const { Title } = Typography;

const GapMembersPage = (props: GapMembersPageProps) => {

  const [providerList, setProviderList] = useState<any>([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [surveyVisible, setSurveyVisible] = useState(false);

  useEffect(() => {
    //initializing dropdown here to only call it once before passing it down to the component
    getProviderKeyListWDates();
    getDistinctProviderKeyListWDates();

    checkSurvey();
    // Create statistic for migration visit
    props.createStatistic(
      'MIGRATION', 
      sessionStorage.getItem('ipaGroup')!, 
      0, 
      'GAP PAGE', 
      sessionStorage.getItem('user_id')!
    );

  }, []);

  //exact logic taken from the legacy code to add in these special distinct providers under gap member report page
  const formatName = (providers: any) => {
    providers.forEach((data: any) => {
      if (data.rmiRevFullName !== null) {
        const splitData = data.rmiRevFullName.split(",");
        data.rmiRevFullName = splitData.join(", ");
      }
      data.Provider = `${getDisplayCompanyId(data.rmiCompanyId)} - (${data.RmiClass})  ${data.rmiRevFullName}`;      
    });

    return providers;
  }

  /**
   * used to set the default provider on the gap member page
   *
   * based on the legacy code logic, there are 4 steps that goes through to filter out and sets the default provider
   * 1) Needs to format the name of the provider (Add "Provider" key to the object)
   * 2) Then it filters out the providers if they have access (term date)
   * 3) Then it filters one more time with these RmiClass
   * 4) It then selects the first one on the list as the default provider...
  */
  const getProviderKeyListWDates = () => {
    return props.getProviderKeyListWDates(sessionStorage.user_id)
      .then((providers: any) => {
        const formatProviders = formatName(providers);

        const checkProviderTerm = formatProviders.filter((provider: any) =>
          provider.RmiClaimAccessDays === null || parseInt(provider.RmiClaimAccessDays) > 0
        );

        const pcps = checkProviderTerm.filter((prov: any) => {
          return (prov.RmiClass === "D" || prov.RmiClass === "P" || prov.RmiClass === "3C" || prov.RmiClass === "C");
        });

        setSelectedProvider(JSON.stringify(pcps[0]));
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  //used for dropdown list on gap member report page
  const getDistinctProviderKeyListWDates = () => {
    return props.getDistinctProviderKeyListWDates(sessionStorage.user_id)
      .then((providers: any) => {
        const formatProviders = formatName(providers);
        const pcps = formatProviders.filter((prov: any) => {
          return (prov.RmiClass === "D" || prov.RmiClass === "P" || prov.RmiClass === "3C" || prov.RmiClass === "C");
        });
        const providerList = sortArrayByKey(pcps, 'Provider');
        setProviderList(providerList); //used for the provider dropdown list
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const checkSurvey = () => {
    return props.checkSurvey('1') // checks if it is time to ask survey
      .then((response: boolean) => {
        setSurveyVisible(!response); // if false, open modal 
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const selectedProviderHandler = (provider: string) => {
    setSelectedProvider(provider);
  }

  return (
    <>
      <div className="hcc-page">
        <div className="header-title">
          <Title level={4} className="ml-3">Gap Members</Title>
        </div>

        <GapMembersSection
          getProviderKeyListWDates={props.getProviderKeyListWDates}
          getDistinctProviderKeyListWDates={props.getDistinctProviderKeyListWDates}
          printProviderMemberReport={props.printProviderMemberReport}
          getProviderHccMembersLob={props.getProviderHccMembersLob}
          getProviderReport={props.getProviderReport}
          getMemberMeasures={props.getMemberMeasures}
          getLastRunDate={props.getLastRunDate}
          getDownloadDocument={props.getDownloadDocument}
          isFetchingEnabledList={props.isFetchingEnabledList}
          isFetchingSpecific={props.isFetchingSpecific}
          isFetchingProvider={props.isFetchingProvider}
          isFetchingProviderHcc={props.isFetchingProviderHcc}
          isFetchingMeasures={props.isFetchingMeasures}
          isFetchingLastRun={props.isFetchingLastRun}
          providerList={providerList}
          selectedProvider={selectedProvider}
          selectedProviderHandler={selectedProviderHandler}

          //eligibility info page related
          search={props.search}
        />

        <ProviderSurveyModal
          visible={surveyVisible}
          setVisible={setSurveyVisible}
          surveyId={'1'}
        />
      </div>
    </>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingEnabledList: store.rootReducer.authProvider.isFetchingEnabledList, // provider dropdown list
  isFetchingSpecific: store.rootReducer.authHCC.isFetchingSpecific, //print HCC summary
  isFetchingProvider: store.rootReducer.eligibilityGAP.isFetchingProvider, // Gap Report table
  isFetchingProviderHcc: store.rootReducer.authProvider.isFetchingProviderHcc, // hcc member table
  isFetchingMeasures: store.rootReducer.authHCC.isFetchingMeasures, //hcc expanded table historical condition
  isFetchingLastRun: store.rootReducer.eligibilityGAP.isFetchingLastRun, // Get the last gap report refresh
  isCheckingSurvey: store.rootReducer.authSurvey.isChecking,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getDistinctProviderKeyListWDates,
    getProviderKeyListWDates,
    printProviderMemberReport,
    getProviderHccMembersLob,
    getProviderReport,
    getMemberMeasures,
    getLastRunDate,
    getDownloadDocument,
    search,
    checkSurvey,
    createStatistic,
  },
  dispatch
);

export default connect(mapStateToProps, mapDispatchToProps)(GapMembersPage);
